import * as THREE from 'three'
import { DUAL_BACKEND_GRAPHICS_REQUIREMENTS } from '@/graphics/GraphicsRequirements'
import type { EngineContext } from '../../../runtime/EngineContext'
import type {
  CharacterCommand,
  CharacterHandle,
  CharacterLookInteraction,
} from '../../../shared/types/character'
import type { Plugin } from '../../../shared/types/plugin'
import type {
  SpatialQueryService,
  ViewEventRouter,
  ViewInputConsumer,
  ViewKeyEvent,
  ViewPointerEvent,
} from '../../../shared/types/services'
import type { SpatialNavigationHit } from '../../../shared/types/spatial'
import type { EngineFrameContext } from '../../../shared/types/view'

/** @internal */
export const CLICK_TO_WALK_MOUSE_CLICK_THRESHOLD_PX = 5
/** @internal */
export const CLICK_TO_WALK_TOUCH_TAP_THRESHOLD_PX = 10
/** @internal */
export const CLICK_TO_WALK_ARRIVAL_RADIUS_M = 0.45
/** @internal */
export const CLICK_TO_WALK_BLOCKED_WINDOW_MS = 900
/** @internal */
export const CLICK_TO_WALK_BLOCKED_MIN_PROGRESS_M = 0.08
/** @internal */
export const CLICK_TO_WALK_TURN_GRACE_MS = 500
/** @internal */
export const CLICK_TO_WALK_ALIGNMENT_DEGREES = 35
/** @internal */
export const CLICK_TO_WALK_HOVER_SURFACE_REUSE_MS = 120
/** @internal */
export const CLICK_TO_WALK_HOVER_SURFACE_REUSE_DISTANCE_PX = 2

const CLICK_TO_WALK_INPUT_PRIORITY = 1_100
const CLICK_TO_WALK_PLUGIN_PRIORITY = 17
const CLICK_TO_WALK_TURN_RATE = 7
const CLICK_TO_WALK_FORWARD_ALIGNMENT_LIMIT_RAD = Math.PI / 2
const MIN_PLANAR_DISTANCE = 1e-4
const MANUAL_INPUT_CODES = new Set([
  'KeyW',
  'KeyA',
  'KeyS',
  'KeyD',
  'ArrowUp',
  'ArrowLeft',
  'ArrowDown',
  'ArrowRight',
  'Space',
  'ShiftLeft',
  'ShiftRight',
])

/** @internal */
export type SpatialCapsuleClickToWalkMode = 'first-person' | 'third-person'

/** @internal */
export type SpatialCapsuleClickToWalkReason =
  | 'arrived'
  | 'blocked'
  | 'context-lost'
  | 'disposed'
  | 'drag'
  | 'invalid-target'
  | 'manual-input'
  | 'mode-change'
  | 'multi-touch'
  | 'pointer-cancel'
  | 'target-set'
  | 'view-removed'
  | 'world-change'

/** @internal */
export interface SpatialCapsuleClickToWalkBinding {
  worldId: string
  primaryViewId: string
  mode: SpatialCapsuleClickToWalkMode
  lookInteraction: CharacterLookInteraction
  character: CharacterHandle | null
  getPrimaryCamera: () => THREE.Camera | null
  isPointerLocked: () => boolean
}

/** @internal */
export interface SpatialCapsuleClickToWalkSnapshot {
  available: boolean
  active: boolean
  hoverPoint: THREE.Vector3 | null
  hoverNormal: THREE.Vector3 | null
  hoverScreen: THREE.Vector2 | null
  targetPoint: THREE.Vector3 | null
  targetNormal: THREE.Vector3 | null
  targetScreen: THREE.Vector2 | null
  lastReason: SpatialCapsuleClickToWalkReason | null
}

interface PointerCandidate {
  pointerId: number
  pointerType: string
  viewId: string
  startScreen: THREE.Vector2
  lastScreen: THREE.Vector2
  threshold: number
  cancelled: boolean
}

interface WalkTarget {
  point: THREE.Vector3
  normal: THREE.Vector3 | null
  screen: THREE.Vector2
  bodyYaw: number
  startedAtMs: number
  blockedWindowStartedAtMs: number | null
  blockedWindowStartDistance: number
}

interface ResolvedNavigationTarget {
  point: THREE.Vector3
  normal: THREE.Vector3 | null
  screen: THREE.Vector2
  navigationHit: SpatialNavigationHit
}

interface ScreenRect {
  x: number
  y: number
  width: number
  height: number
}

interface SurfacePickQuery {
  screen: THREE.Vector2
  viewId: string
  viewRect: ScreenRect
  viewport?: ScreenRect
}

interface ResolvedSurfaceTarget {
  point: THREE.Vector3
  normal: THREE.Vector3 | null
  screen: THREE.Vector2
  worldId: string
}

interface CachedSurfaceTarget extends ResolvedSurfaceTarget {
  viewId: string
  pickedAtMs: number
}

type SnapshotListener = (snapshot: SpatialCapsuleClickToWalkSnapshot) => void

/** Click-to-walk controller for spatially navigated capsule characters. */
export class SpatialCapsuleClickToWalkController implements Plugin {
  readonly name = 'spatial-capsule-click-to-walk'
  readonly graphicsRequirements = DUAL_BACKEND_GRAPHICS_REQUIREMENTS
  readonly priority = CLICK_TO_WALK_PLUGIN_PRIORITY
  readonly dependencies = ['ViewEventRouter', 'SpatialQueryService'] as const

  private readonly listeners = new Set<SnapshotListener>()
  private readonly activeTouchPointers = new Set<number>()
  private readonly manualKeys = new Set<string>()
  private readonly consumer: ViewInputConsumer = {
    name: 'spatial-capsule-click-to-walk-input',
    priority: CLICK_TO_WALK_INPUT_PRIORITY,
    onPointerDown: (event) => this.handlePointerDown(event),
    onPointerMove: (event) => this.handlePointerMove(event),
    onPointerUp: (event) => this.handlePointerUp(event),
    onWinKeyDown: (event) => this.handleKeyDown(event),
    onWinKeyUp: (event) => this.handleKeyUp(event),
  }

  private context: EngineContext | null = null
  private viewEventRouter: ViewEventRouter | null = null
  private spatial: SpatialQueryService | null = null
  private binding: SpatialCapsuleClickToWalkBinding | null = null
  private pointerCandidate: PointerCandidate | null = null
  private target: WalkTarget | null = null
  private pendingHoverPick: SurfacePickQuery | null = null
  private cachedSurfaceTarget: CachedSurfaceTarget | null = null
  private hoverPoint: THREE.Vector3 | null = null
  private hoverNormal: THREE.Vector3 | null = null
  private hoverScreen: THREE.Vector2 | null = null
  private lastReason: SpatialCapsuleClickToWalkReason | null = null
  private disposed = false

  onInit(context: EngineContext): void {
    this.context = context
  }

  onStart(): void {
    if (!this.context) {
      throw new Error('SpatialCapsuleClickToWalkController is not initialized')
    }

    this.viewEventRouter = this.context.getService('ViewEventRouter')
    this.spatial = this.context.getService('SpatialQueryService')
    this.viewEventRouter.addConsumer(this.consumer)
    this.context.events.on('view.removed', this.handleViewRemoved)
  }

  onUpdateFrame(frame: EngineFrameContext): void {
    this.updateHoverSurfacePick()

    if (!this.target) {
      return
    }

    const binding = this.binding
    if (!binding?.character || !this.isAvailable()) {
      this.cancelTarget('context-lost')
      return
    }

    if (this.manualKeys.size > 0) {
      this.cancelTarget('manual-input', { writeNeutralCommand: false })
      return
    }

    const snapshot = binding.character.getSnapshot()
    const command = binding.character.getCommand()
    const planarDelta = this.target.point.clone().sub(snapshot.floorPoint)
    planarDelta.z = 0

    const distance = planarDelta.length()
    if (distance <= CLICK_TO_WALK_ARRIVAL_RADIUS_M) {
      this.cancelTarget('arrived')
      return
    }

    if (distance <= MIN_PLANAR_DISTANCE) {
      this.cancelTarget('arrived')
      return
    }

    const targetYaw = Math.atan2(planarDelta.y, planarDelta.x)
    const nextYaw = rotateTowardsAngle(
      this.target.bodyYaw,
      targetYaw,
      Math.max(0, frame.delta) * CLICK_TO_WALK_TURN_RATE
    )
    this.target.bodyYaw = nextYaw
    const alignment = Math.abs(shortestAngleDelta(nextYaw, targetYaw))
    const forward = alignment < CLICK_TO_WALK_FORWARD_ALIGNMENT_LIMIT_RAD ? 1 : 0

    if (this.isBlocked(frame.timestamp, distance, alignment)) {
      this.cancelTarget('blocked')
      return
    }

    binding.character.setCommand({
      move: {
        forward,
        right: 0,
      },
      run: forward > 0,
      jump: false,
      bodyYaw: nextYaw,
      aimYaw: command.aimYaw,
      aimPitch: command.aimPitch,
    })
  }

  onDestroy(): void {
    this.disposed = true
    this.cancelTarget('disposed')
    this.clearHoverPickState()
    this.clearHover()
    this.manualKeys.clear()
    this.activeTouchPointers.clear()
    this.pointerCandidate = null
    this.context?.events.off('view.removed', this.handleViewRemoved)
    this.viewEventRouter?.removeConsumer(this.consumer)
    this.viewEventRouter = null
    this.spatial = null
    this.context = null
    this.binding = null
    this.listeners.clear()
  }

  bind(binding: SpatialCapsuleClickToWalkBinding | null): void {
    const previous = this.binding
    this.binding = binding

    if (!binding) {
      this.cancelTarget('context-lost')
      this.clearHoverPickState()
      this.clearHover()
      this.emitSnapshot()
      return
    }

    if (previous?.worldId && previous.worldId !== binding.worldId) {
      this.cancelTarget('world-change')
    } else if (previous?.primaryViewId && previous.primaryViewId !== binding.primaryViewId) {
      this.cancelTarget('view-removed')
    } else if (previous?.mode && previous.mode !== binding.mode) {
      this.cancelTarget('mode-change')
    }

    this.emitSnapshot()
  }

  getSnapshot(): SpatialCapsuleClickToWalkSnapshot {
    return {
      available: this.isAvailable(),
      active: !!this.target,
      hoverPoint: this.hoverPoint?.clone() ?? null,
      hoverNormal: this.hoverNormal?.clone() ?? null,
      hoverScreen: this.hoverScreen?.clone() ?? null,
      targetPoint: this.target?.point.clone() ?? null,
      targetNormal: this.target?.normal?.clone() ?? null,
      targetScreen: this.target?.screen.clone() ?? null,
      lastReason: this.lastReason,
    }
  }

  onSnapshotChange(listener: SnapshotListener): () => void {
    this.listeners.add(listener)
    listener(this.getSnapshot())
    return () => {
      this.listeners.delete(listener)
    }
  }

  private handlePointerDown(event: ViewPointerEvent): boolean {
    const pointerId = event.domEvent.pointerId
    const pointerType = event.domEvent.pointerType || 'mouse'

    if (pointerType === 'touch') {
      this.activeTouchPointers.add(pointerId)
      if (this.activeTouchPointers.size > 1) {
        this.pointerCandidate = null
        this.cancelTarget('multi-touch')
        return false
      }
    }

    if (!this.shouldTrackPointer(event)) {
      return false
    }

    this.pointerCandidate = {
      pointerId,
      pointerType,
      viewId: event.viewId,
      startScreen: event.screen.clone(),
      lastScreen: event.screen.clone(),
      threshold:
        pointerType === 'touch'
          ? CLICK_TO_WALK_TOUCH_TAP_THRESHOLD_PX
          : CLICK_TO_WALK_MOUSE_CLICK_THRESHOLD_PX,
      cancelled: false,
    }
    this.clearPendingHoverPick()
    return false
  }

  private handlePointerMove(event: ViewPointerEvent): boolean {
    const candidate = this.pointerCandidate
    if (candidate && candidate.pointerId === event.domEvent.pointerId) {
      candidate.lastScreen.copy(event.screen)
      if (
        !candidate.cancelled &&
        event.screen.distanceTo(candidate.startScreen) > candidate.threshold
      ) {
        candidate.cancelled = true
        this.clearHoverPickState()
        this.clearHover()
      }
      return false
    }

    if (!this.shouldPickHover(event)) {
      this.clearHoverPickState()
      this.clearHover()
      return false
    }

    this.queueHoverSurfacePick(event)
    return false
  }

  private handlePointerUp(event: ViewPointerEvent): boolean {
    const pointerId = event.domEvent.pointerId
    const pointerType = event.domEvent.pointerType || 'mouse'
    if (pointerType === 'touch') {
      this.activeTouchPointers.delete(pointerId)
    }

    const candidate = this.pointerCandidate
    if (!candidate || candidate.pointerId !== pointerId) {
      return false
    }

    this.pointerCandidate = null

    if (event.domEvent.type === 'pointercancel') {
      this.cancelTarget('pointer-cancel')
      return false
    }

    if (
      candidate.cancelled ||
      candidate.viewId !== event.viewId ||
      event.screen.distanceTo(candidate.startScreen) > candidate.threshold
    ) {
      return false
    }

    if (this.manualKeys.size > 0) {
      this.cancelTarget('manual-input', { writeNeutralCommand: false })
      return false
    }

    const resolved = this.resolveNavigationTarget(event)
    if (!resolved) {
      if (this.target) {
        this.cancelTarget('invalid-target')
      } else {
        this.lastReason = 'invalid-target'
        this.emitSnapshot()
      }
      return false
    }

    this.setTarget(resolved)
    return false
  }

  private handleKeyDown(event: ViewKeyEvent): boolean {
    if (!this.isPrimaryKeyEvent(event) || !MANUAL_INPUT_CODES.has(event.code)) {
      return false
    }

    this.manualKeys.add(event.code)
    if (this.target) {
      this.cancelTarget('manual-input', { writeNeutralCommand: false })
    }
    return false
  }

  private handleKeyUp(event: ViewKeyEvent): boolean {
    if (MANUAL_INPUT_CODES.has(event.code)) {
      this.manualKeys.delete(event.code)
    }
    return false
  }

  private readonly handleViewRemoved = ({ viewId }: { viewId: string }) => {
    if (viewId !== this.binding?.primaryViewId) {
      return
    }

    this.cancelTarget('view-removed')
    this.clearHover()
  }

  private shouldTrackPointer(event: ViewPointerEvent): boolean {
    return event.button === 0 && this.isPrimaryViewEvent(event) && this.isAvailable()
  }

  private isPrimaryViewEvent(event: ViewPointerEvent): boolean {
    return this.isPrimaryViewId(event.viewId)
  }

  private isPrimaryKeyEvent(event: ViewKeyEvent): boolean {
    return event.viewId === this.binding?.primaryViewId
  }

  private isPrimaryViewId(viewId: string): boolean {
    return viewId === this.binding?.primaryViewId
  }

  private isAvailable(): boolean {
    const binding = this.binding
    return (
      !this.disposed &&
      !!this.spatial &&
      !!binding?.character &&
      binding.worldId.length > 0 &&
      binding.primaryViewId.length > 0 &&
      !binding.isPointerLocked() &&
      !(binding.mode === 'first-person' && binding.lookInteraction === 'pointer-lock')
    )
  }

  private shouldPickHover(event: ViewPointerEvent): boolean {
    return (
      this.isPrimaryViewEvent(event) &&
      this.isAvailable() &&
      event.domEvent.pointerType !== 'touch' &&
      (event.domEvent.buttons ?? 0) === 0
    )
  }

  private queueHoverSurfacePick(event: ViewPointerEvent): void {
    this.pendingHoverPick = {
      screen: event.screen.clone(),
      viewId: event.viewId,
      viewRect: { ...event.viewRect },
      viewport: event.viewport ? { ...event.viewport } : undefined,
    }
  }

  private updateHoverSurfacePick(): void {
    if (!this.pendingHoverPick) {
      return
    }

    const query = this.pendingHoverPick
    this.pendingHoverPick = null

    const resolved = this.resolveSurfaceTarget(query)
    if (!resolved) {
      this.cachedSurfaceTarget = null
      this.clearHover()
      return
    }

    this.cachedSurfaceTarget = {
      ...resolved,
      viewId: query.viewId,
      pickedAtMs: performance.now(),
    }
    this.hoverPoint = resolved.point.clone()
    this.hoverNormal = resolved.normal?.clone() ?? null
    this.hoverScreen = resolved.screen.clone()
    this.emitSnapshot()
  }

  private clearPendingHoverPick(): void {
    this.pendingHoverPick = null
  }

  private clearHoverPickState(): void {
    this.pendingHoverPick = null
    this.cachedSurfaceTarget = null
  }

  private resolveNavigationTarget(event: ViewPointerEvent): ResolvedNavigationTarget | null {
    const binding = this.binding
    if (!this.spatial || !binding || !this.isPrimaryViewEvent(event)) {
      return null
    }

    const surfaceTarget = this.getReusableSurfaceTarget(event) ?? this.resolveSurfaceTarget(event)
    if (!surfaceTarget || surfaceTarget.worldId !== binding.worldId) {
      return null
    }

    const navigationHit = this.spatial.projectNavigationPoint({
      worldId: binding.worldId,
      point: surfaceTarget.point,
    })
    if (!navigationHit?.walkable || navigationHit.worldId !== binding.worldId) {
      return null
    }

    return {
      point: navigationHit.point.clone(),
      normal: normalizeHitNormal(navigationHit.normal) ?? surfaceTarget.normal?.clone() ?? null,
      screen: event.screen.clone(),
      navigationHit,
    }
  }

  private resolveSurfaceTarget(query: SurfacePickQuery): ResolvedSurfaceTarget | null {
    const binding = this.binding
    const camera = binding?.getPrimaryCamera()
    if (!this.spatial || !binding || !camera || !this.isPrimaryViewId(query.viewId)) {
      return null
    }

    const surfaceHit = this.spatial.raycastSurfaceAtScreen({
      surfaceUsage: 'camera-control',
      camera,
      screen: query.screen.clone(),
      worldId: binding.worldId,
      viewId: query.viewId,
      viewRect: { ...query.viewRect },
      viewport: query.viewport ? { ...query.viewport } : { ...query.viewRect },
    })
    if (!surfaceHit || surfaceHit.worldId !== binding.worldId) {
      return null
    }

    return {
      point: surfaceHit.point.clone(),
      normal: normalizeHitNormal(surfaceHit.normal),
      screen: query.screen.clone(),
      worldId: surfaceHit.worldId,
    }
  }

  private getReusableSurfaceTarget(event: ViewPointerEvent): ResolvedSurfaceTarget | null {
    const cached = this.cachedSurfaceTarget
    const binding = this.binding
    if (!cached || !binding || cached.worldId !== binding.worldId) {
      return null
    }

    if (cached.viewId !== event.viewId) {
      return null
    }

    if (
      performance.now() - cached.pickedAtMs > CLICK_TO_WALK_HOVER_SURFACE_REUSE_MS ||
      cached.screen.distanceTo(event.screen) > CLICK_TO_WALK_HOVER_SURFACE_REUSE_DISTANCE_PX
    ) {
      return null
    }

    return {
      point: cached.point.clone(),
      normal: cached.normal?.clone() ?? null,
      screen: event.screen.clone(),
      worldId: cached.worldId,
    }
  }

  private setTarget(resolved: ResolvedNavigationTarget): void {
    this.clearHoverPickState()
    const command = this.binding?.character?.getCommand()
    this.target = {
      point: resolved.point.clone(),
      normal: resolved.normal?.clone() ?? null,
      screen: resolved.screen.clone(),
      bodyYaw: this.target?.bodyYaw ?? command?.bodyYaw ?? 0,
      startedAtMs: performance.now(),
      blockedWindowStartedAtMs: null,
      blockedWindowStartDistance: Number.POSITIVE_INFINITY,
    }
    this.clearHover(false)
    this.lastReason = 'target-set'
    this.emitSnapshot()
  }

  private cancelTarget(
    reason: SpatialCapsuleClickToWalkReason,
    options: { writeNeutralCommand?: boolean } = {}
  ): void {
    const hadTarget = !!this.target
    this.target = null
    this.pointerCandidate = null
    this.clearHoverPickState()
    this.clearHover(false)
    this.lastReason = reason
    if (hadTarget && options.writeNeutralCommand !== false) {
      this.writeNeutralCommand()
    }
    this.emitSnapshot()
  }

  private writeNeutralCommand(): void {
    const character = this.binding?.character
    if (!character) {
      return
    }

    const command = character.getCommand()
    character.setCommand(createNeutralCommand(command))
  }

  private clearHover(emit = true): void {
    if (!this.hoverPoint && !this.hoverScreen) {
      return
    }

    this.hoverPoint = null
    this.hoverNormal = null
    this.hoverScreen = null
    if (emit) {
      this.emitSnapshot()
    }
  }

  private isBlocked(timestamp: number, distance: number, alignment: number): boolean {
    if (!this.target) {
      return false
    }

    const turnGraceElapsed = timestamp - this.target.startedAtMs >= CLICK_TO_WALK_TURN_GRACE_MS
    const aligned = THREE.MathUtils.radToDeg(alignment) <= CLICK_TO_WALK_ALIGNMENT_DEGREES
    if (!turnGraceElapsed || !aligned) {
      this.target.blockedWindowStartedAtMs = null
      this.target.blockedWindowStartDistance = distance
      return false
    }

    if (this.target.blockedWindowStartedAtMs === null) {
      this.target.blockedWindowStartedAtMs = timestamp
      this.target.blockedWindowStartDistance = distance
      return false
    }

    const elapsed = timestamp - this.target.blockedWindowStartedAtMs
    const progress = this.target.blockedWindowStartDistance - distance
    if (progress >= CLICK_TO_WALK_BLOCKED_MIN_PROGRESS_M) {
      this.target.blockedWindowStartedAtMs = timestamp
      this.target.blockedWindowStartDistance = distance
      return false
    }

    return elapsed >= CLICK_TO_WALK_BLOCKED_WINDOW_MS
  }

  private emitSnapshot(): void {
    const snapshot = this.getSnapshot()
    for (const listener of this.listeners) {
      listener(snapshot)
    }
  }
}

function createNeutralCommand(command: CharacterCommand): Partial<CharacterCommand> {
  return {
    move: {
      forward: 0,
      right: 0,
    },
    run: false,
    jump: false,
    bodyYaw: command.bodyYaw,
    aimYaw: command.aimYaw,
    aimPitch: command.aimPitch,
  }
}

function rotateTowardsAngle(current: number, target: number, maxStep: number): number {
  const delta = shortestAngleDelta(current, target)
  return current + THREE.MathUtils.clamp(delta, -maxStep, maxStep)
}

function shortestAngleDelta(current: number, target: number): number {
  return Math.atan2(Math.sin(target - current), Math.cos(target - current))
}

function normalizeHitNormal(normal: THREE.Vector3 | null | undefined): THREE.Vector3 | null {
  if (!normal) {
    return null
  }

  const next = normal.clone()
  if (next.lengthSq() <= Number.EPSILON) {
    return null
  }

  return next.normalize()
}
